import Vue from 'vue';
import Vuex, { Store } from 'vuex';
import VuexPersist from 'vuex-persist';

import { settings } from '@/settings/store/settings.store';
import { contractManagement } from '@/contract-management/store/contract-management.store';
import actions from '@/store/actions';
import store from '@/store/stores';
import { archive } from '@/store/stores/archive.store';
import { alert } from '@/store/stores/alert.store';
import { documentView } from '@/store/stores/document-view.store';
import { documentEdit } from '@/store/stores/document-edit.store';
import { documentSettings } from '@/store/stores/document-settings.store';
import { documents } from '@/store/stores/documents.store';
import { feedback } from '@/store/stores/feedback.store';
import { notifications } from '@/store/stores/notifications.store';
import { report } from '@/store/stores/report.store';
import { session } from '@/store/stores/session.store';
import { settingsBilling } from '@/store/stores/settings-billing.store';
import { uploadVideo } from '@/store/stores/upload-video.store';
import { user } from '@/store/stores/user.store';
import {
  ContactEditSettings,
  TemplateRoleSettings,
  RecipientEditSettings,
  SettingsIntegrationEnum,
  SettingsEnum,
  DocumentEnum,
  DocumentData,
  FieldActionData,
  DealActionData,
  AttachmentActionData,
  SettingsData,
  DocumentNotificationData,
  DocumentOwnerData,
  ArchiveUploadData,
} from '@/store/stores/modal.store';
import { editor } from '@/editor/store/editor.store';
import { dealboard } from '@/dealboard/store/dealboard.store';
import { entity } from '@/store/stores/entity.store';
import { tags } from '@/tags/store/tag.store';
import { dealroom } from '@/dealroom/store/dealroom.store';
import { dealroomView } from '@/dealroom-view/store/dealroom-view.store';
import { EnumPlans } from '@/enums/Plans';
import { video } from '@/video/store/video.store';
import { search } from '@/topbar/search/store/search.store';
import { onboarding } from '@/onboarding-sidebar/store/onboarding.store';
import { betaFlag } from '@/beta-flag/store/beta-flag.store';
import { contentLibrary } from '@/content-library/store/content-library.store';
import { chat } from '@/dealroom-view/chat/store/chat.store';
import { contentLink } from '@/content-link/store/content-link.store';
import { googleDocs } from '@/google-docs/store/google-docs.store';
import { threshold } from '@/threshold/store/threshold.store';

Vue.use(Vuex);

export interface RootState {
  modalIsLoading: boolean;
  showArchiveUpload: boolean;
  archiveUploadData: ArchiveUploadData;
  showContactEdit: boolean;
  contactEditSettings: ContactEditSettings;
  showTemplateRole: boolean;
  templateRoleSettings: TemplateRoleSettings;
  showRecipientEdit: boolean;
  recipientEditSettings: RecipientEditSettings;
  showSettings: SettingsEnum;
  showSettingsIntegration: SettingsIntegrationEnum;
  showDocument: DocumentEnum;
  documentData: DocumentData;
  showFieldAction: boolean;
  fieldActionData: FieldActionData;
  showDealAction: boolean;
  dealActionData: DealActionData;
  attachmentActionData: AttachmentActionData;
  settingsData: SettingsData;
  showDocumentNotification: boolean;
  documentNotificationData: DocumentNotificationData;
  showLoginForm: boolean;
  showDocumentOwner: boolean;
  documentOwnerData: DocumentOwnerData;
  upgradeModalPlan: EnumPlans;
  contentIsLoading: boolean;
  showAccessDeniedModal: boolean;
}

export const state: RootState = {
  ...store.state,
};

export const mutations = {
  ...store.mutations,
};

const vuexLocalStorage = new VuexPersist({
  key: 'GA_SESSION',
  storage: window.localStorage,
  reducer: (state: any) => ({
    session: {
      session: state.session.session,
      token: state.session.token,
    },
    report: {
      showAll: state.report.showAll,
      showTeam: state.report.showTeam,
    },
    archive: {
      groupFilter: state.archive.groupFilter,
    },
    contractManagement: {
      filter: state.contractManagement.filter,
      folder: state.contractManagement.folder,
    },
    betaFlag: {
      dismissedBetaExpires: state.betaFlag.dismissedBetaExpires,
      flags: {
        contentLibrary: state.betaFlag.flags.contentLibrary,
      },
    },
  }),
});
const vuexSessionStorage = new VuexPersist({
  key: `GA_SESSION`,
  storage: window.sessionStorage,
  reducer: (state: any) => ({
    threshold: {
      dismissedActiveDocumentThresholdSnackbar:
        state.threshold.dismissedActiveDocumentThresholdSnackbar,
    },
  }),
});

export default new Store<RootState>({
  mutations,
  actions,
  state,
  modules: {
    alert,
    archive,
    contractManagement,
    contentLibrary,
    dealboard,
    settings,
    dealroom,
    dealroomView,
    documentEdit,
    documentSettings,
    documentView,
    entity,
    feedback,
    notifications,
    report,
    session,
    settingsBilling,
    uploadVideo,
    user,
    tags,
    editor,
    documents,
    video,
    search,
    onboarding,
    betaFlag,
    chat,
    contentLink,
    googleDocs,
    threshold,
  },
  plugins: [vuexLocalStorage.plugin, vuexSessionStorage.plugin],
});
